import React, { useRef, useState } from "react";
import axios from "axios";
import { Upload, Loader2, X, ImageIcon } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_MB = 5;

/**
 * Reusable image uploader for the admin dashboard.
 * - Calls POST /api/admin/upload (JWT required)
 * - On success, calls onChange(publicUrl) with the absolute URL to display
 * - Shows live preview and a "Supprimer" button
 *
 * Props:
 *   value       — current image URL (string | null)
 *   onChange    — (newUrl: string | null) => void
 *   folder      — "testimonials" | "events"
 *   authHeader  — function returning {Authorization: 'Bearer ...'}
 *   label       — optional label override
 *   testId      — prefix for data-testid attributes
 */
const ImageUploader = ({ value, onChange, folder = "testimonials", authHeader, label, testId = "image-uploader" }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Le fichier doit être une image (JPG, PNG, WebP, GIF)");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`Image trop volumineuse (max ${MAX_MB} MB)`);
      e.target.value = "";
      return;
    }

    const data = new FormData();
    data.append("file", file);

    setUploading(true);
    try {
      const { data: res } = await axios.post(
        `${API}/admin/upload?folder=${encodeURIComponent(folder)}`,
        data,
        { headers: { ...authHeader(), "Content-Type": "multipart/form-data" } }
      );
      // res.url is a relative path "/api/files/<path>" — build absolute URL
      const absoluteUrl = `${process.env.REACT_APP_BACKEND_URL}${res.url}`;
      onChange(absoluteUrl);
      toast.success("Image téléversée");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Échec de l'upload";
      toast.error("Erreur", { description: msg });
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div data-testid={testId}>
      {label && (
        <div className="text-navy-700 text-[11px] uppercase tracking-[0.18em] font-semibold mb-2">
          {label}
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        onChange={handleSelect}
        className="hidden"
        data-testid={`${testId}-input`}
      />

      {value ? (
        <div className="flex items-center gap-4 bg-[#FAF8F3] border border-navy-100 p-3">
          <img
            src={value}
            alt="Aperçu"
            className="w-20 h-20 object-cover ring-2 ring-red-500/40"
            data-testid={`${testId}-preview`}
          />
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-[0.18em] text-navy-500 font-semibold mb-1">Image téléversée</div>
            <div className="text-[11px] text-[#888] truncate">{value.split("/").pop()}</div>
            <div className="flex gap-2 mt-2">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid={`${testId}-replace`}
                className="text-[10px] uppercase tracking-[0.18em] font-semibold text-navy-700 hover:text-red-500 border border-navy-200 hover:border-red-500 px-2.5 py-1.5 inline-flex items-center gap-1.5"
              >
                {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                Remplacer
              </button>
              <button
                type="button"
                onClick={() => onChange(null)}
                disabled={uploading}
                data-testid={`${testId}-remove`}
                className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#a33] hover:bg-[#fdecee] border border-[#ddd] hover:border-[#a33] px-2.5 py-1.5 inline-flex items-center gap-1.5"
              >
                <X className="h-3 w-3" /> Supprimer
              </button>
            </div>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid={`${testId}-trigger`}
          className="w-full flex flex-col items-center justify-center gap-2 py-7 border-2 border-dashed border-navy-100 hover:border-navy-500 hover:bg-[#FAF8F3] transition-colors text-navy-500 disabled:opacity-50"
        >
          {uploading ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-[12px] uppercase tracking-[0.18em] font-semibold">Téléversement…</span>
            </>
          ) : (
            <>
              <div className="w-10 h-10 bg-navy-50 flex items-center justify-center">
                <ImageIcon className="h-4.5 w-4.5" strokeWidth={1.6} />
              </div>
              <span className="text-[12px] uppercase tracking-[0.18em] font-semibold text-navy-700">Choisir une image</span>
              <span className="text-[10.5px] text-[#888] normal-case tracking-normal">JPG, PNG, WebP, GIF · 5 MB max</span>
            </>
          )}
        </button>
      )}
    </div>
  );
};

export default ImageUploader;
